import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { catchError, from, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments';
import { THEME_SIGNAL } from '../../interfaces/theme';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { LANGUAGE_CONTENT_TOKEN } from '../../tokens/server-tokens';

export const INITIAL_TRANSLATION = makeStateKey<[string, TranslationObject]>('INITIAL_TRANSLATION');

@Injectable({
  providedIn: 'root',
})
export class CustomTranslateLoader implements TranslateLoader {
  protected platform = inject(PLATFORM_ID);

  protected languageContentManager = inject(LANGUAGE_CONTENT_TOKEN, { optional: true });

  protected cacheService = inject(CacheService);

  protected http = inject(HttpClient);

  protected apiConfig = inject(ApiConfigService);

  public internalCache = new Map<string, TranslationObject>();

  public transferState = inject(TransferState);

  private readonly theme = inject(THEME_SIGNAL);

  constructor() {
    this.setupInitialTranslation();
  }

  public setupInitialTranslation() {
    if (!isPlatformBrowser(this.platform)) {
      return;
    }

    const initialTranslation = this.transferState.get(INITIAL_TRANSLATION, null);
    if (!initialTranslation) {
      return;
    }

    this.internalCache.set(initialTranslation[0], initialTranslation[1]);
  }

  getTranslation(lang: string): Observable<TranslationObject> {
    if (
      lang === 'en' &&
      (environment as never as { useFallbackEnJson?: boolean }).useFallbackEnJson
    ) {
      return this.temporaryEnLoader();
    }

    return isPlatformBrowser(this.platform)
      ? this.fetchFromServer(lang)
      : this.fetchInsideServer(lang);
  }

  /**
   * Fetches from the server
   * @param language
   * @returns
   */
  public fetchFromServer(language: string): Observable<TranslationObject> {
    const cacheContent = this.cacheService.cachedContent();

    const internalCache = this.internalCache.get(language);

    if (internalCache) {
      return of(internalCache);
    }

    return this.http
      .get<TranslationObject>(this.apiConfig.hostApiUrl('language-translations'), {
        params: {
          host: globalThis.location.host,
          s3Bucket: cacheContent?.cache.config.webAppConfig.s3BucketAssets || '',
          language,
          customerId: cacheContent?.cache.customerId || '',
          theme: this.theme() || '',
        },
      })
      .pipe(
        tap({
          next: (translation) => {
            this.internalCache.set(language, translation);
          },
        })
      )
      .pipe(catchError(() => of({})));
  }

  public fetchInsideServer(language: string): Observable<TranslationObject> {
    if (!this.languageContentManager) {
      throw new Error('language content manager not provided in server context');
    }

    const cacheContent = this.cacheService.cachedContent();

    if (!cacheContent) {
      throw new Error('Cache content not initialized');
    }

    return from(
      this.languageContentManager.fetchLanguage(
        cacheContent.cache.config.webAppConfig.s3BucketAssets,
        cacheContent.cache.customerId,
        this.cacheService.getHost(),
        language,
        this.theme()
      )
    )
      .pipe(
        map((result) => {
          this.transferState.set(INITIAL_TRANSLATION, [language, result]);
          return result;
        }),
        tap({
          error: (error) => {
            console.warn('CustomTranslateLoader: Failed to fetch translation from bucket');
            console.error(error);
          },
        })
      )
      .pipe(catchError(() => of({})));
  }

  public temporaryEnLoader(): Observable<TranslationObject> {
    const existingData = this.internalCache.get('en');

    return existingData
      ? of(existingData)
      : this.http
          .get<TranslationObject>(this.apiConfig.hostUrl(`assets/${this.theme()}/i18n/en.json`))
          .pipe(
            tap({
              next: (value) => this.internalCache.set('en', value),
            })
          )
          .pipe(catchError(() => of({})));
  }
}
